import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import { useAudioSettings } from "@/lib/audio-settings";
import { nextSound } from "@/lib/sound-order";
import { playClip, setCabinLevel } from "@/lib/cabin-audio";

/**
 * SoundPlayer
 * -----------
 * Watches for `phase` prop changes. On each new phase, looks up the current
 * flight's operator soundpack and plays the matching audio clip once, through
 * the app-wide cabin channel (lib/cabin-audio.js) so no page change cuts it off.
 *
 * Volume + mute come from `useAudioSettings` (persisted to localStorage), so
 * the volume slider in the sidebar controls playback in real time.
 */
export default function SoundPlayer({ phase, operator, flightId = null, enabled = true }) {
  const lastPhaseRef = useRef(null);
  const reachedRef = useRef(-1);   // furthest SOUND_ORDER index this flight has reached
  // API_BASE, not the raw env var. Unset — which is the normal same-origin
  // deployment — `process.env.REACT_APP_BACKEND_URL` is undefined and this
  // built "undefined/api/sounds/file/x.mp3", so no clip has ever played in
  // the browser build. api.js normalises this to "" for exactly this reason;
  // rebuilding the base by hand walked straight back into it.
  const backendBase = API_BASE;
  const [{ volume, muted }] = useAudioSettings();

  // Per-phase auto-trigger switches (migration 009). Absent or unset means on,
  // so a pilot who has never opened the Audio page hears what they always did,
  // and a phase added to sounds.PHASES later is not silently muted for anyone.
  const { data: prefs } = useQuery({
    queryKey: ["pilot-prefs"],
    queryFn: () => api.get("/pilot/prefs").then((r) => r.data),
    staleTime: 60_000,
  });

  const { data: pack } = useQuery({
    queryKey: ["operator-pack", operator],
    queryFn: () => (operator ? api.get(`/sounds/packs/for-operator/${operator}`).then((r) => r.data) : null),
    enabled: !!operator,
    staleTime: 60_000,
  });

  // A new flight starts from no phase, so its BOARDING plays even when the
  // last flight also began at BOARDING. Nothing playing is stopped here.
  useEffect(() => {
    lastPhaseRef.current = null;
    reachedRef.current = -1;
  }, [flightId]);

  // Live-adjust volume/mute on the clip that is playing. The channel is shared
  // with the Audio page's soundboard (lib/cabin-audio.js); this component is
  // always mounted, so it keeps the level in step for both.
  useEffect(() => {
    setCabinLevel({ volume, muted });
  }, [volume, muted]);

  useEffect(() => {
    if (!enabled || !phase || !pack) return;
    if (phase === lastPhaseRef.current) return;
    lastPhaseRef.current = phase;
    const { key, reached } = nextSound(phase, reachedRef.current);
    if (!key) return;   // CLIMB, TAKEOFF (no clip), or a step back
    reachedRef.current = reached;
    if (prefs?.sound_auto?.[key] === false) return;   // pilot silenced this one
    const meta = pack.sounds?.[key];
    if (!meta?.filename) return;
    const url = `${backendBase}/api/sounds/file/${meta.filename}`;
    playClip(url, key).catch(() => { /* autoplay blocked or file missing; the next phase tries again */ });
  }, [phase, pack, enabled, backendBase, prefs]);

  return null;
}
